import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Github, AlertTriangle } from 'lucide-react';
import api from '../../utils/api';

export const meta = {
  id: 'github_actions',
  category: 'system',
  titleKey: 'dashboard.widgets.githubActions',
  descriptionKey: 'dashboard.widgets.githubActionsDesc',
  defaultSize: 'sm',
  minRole: 'admin',
  icon: Github,
};

// Format the reset date using its UTC calendar day (the cycle resets at
// 00:00 UTC on the 1st — rendering in local time would slip it to the prior
// day for AR/UTC-3). See timezone conventions: pure-date display uses UTC parts.
function formatResetDate(iso, locale) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

function Header({ t }) {
  return (
    <div className="card-header">
      <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Github size={16} />
        {t('dashboard.widgets.githubActions')}
      </div>
    </div>
  );
}

export default function GitHubActionsWidget() {
  const { t, i18n } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: ['github-actions-usage'],
    queryFn: () => api.get('/dashboard/github-actions').then(r => r.data),
    refetchInterval: 15 * 60 * 1000,
    staleTime: 10 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="card" style={{ height: '100%' }}>
        <Header t={t} />
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 13 }}>…</div>
      </div>
    );
  }

  // Not configured → tell the admin how to enable it.
  if (data && data.configured === false) {
    return (
      <div className="card" style={{ height: '100%' }}>
        <Header t={t} />
        <div style={{ padding: '12px 20px 16px', fontSize: 12, color: 'var(--c-text-muted)', lineHeight: 1.5 }}>
          {t('dashboard.widgets.githubActionsNotConfigured')}
        </div>
      </div>
    );
  }

  // Token present but the call failed (bad scope / wrong owner / GitHub down).
  if (!data || data.error) {
    const reason = data?.error === 'auth'
      ? t('dashboard.widgets.githubActionsErrAuth')
      : data?.error === 'not_found'
        ? t('dashboard.widgets.githubActionsErrNotFound')
        : t('dashboard.widgets.githubActionsErrFetch');
    return (
      <div className="card" style={{ height: '100%' }}>
        <Header t={t} />
        <div style={{ padding: '12px 20px 16px', display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: 'var(--c-text-muted)' }}>
          <AlertTriangle size={14} color="#f59e0b" style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{reason}</span>
        </div>
      </div>
    );
  }

  const { included, used, remaining, percentUsed, resetDate, overage } = data;
  // Bar color shifts to amber/red as the quota fills up.
  const barColor = percentUsed >= 90 ? '#ef4444' : percentUsed >= 75 ? '#f59e0b' : '#22c55e';

  return (
    <div className="card" style={{ height: '100%' }}>
      <Header t={t} />
      <div style={{ padding: '12px 20px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 26, fontWeight: 700, color: barColor }}>
            {remaining}
          </span>
          <span style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>
            {t('dashboard.widgets.githubActionsLeft')}
          </span>
        </div>

        {/* Progress bar: used vs included */}
        <div>
          <div style={{ height: 8, borderRadius: 4, background: 'var(--c-surface-2)', overflow: 'hidden' }}>
            <div style={{ width: `${percentUsed}%`, height: '100%', background: barColor, transition: 'width .3s' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 12, color: 'var(--c-text-muted)' }}>
            <span>{t('dashboard.widgets.githubActionsUsed', { used, included })}</span>
            <span>{percentUsed}%</span>
          </div>
        </div>

        {overage > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#ef4444' }}>
            <AlertTriangle size={13} style={{ flexShrink: 0 }} />
            <span>{t('dashboard.widgets.githubActionsOverage', { amount: overage })}</span>
          </div>
        )}

        <div style={{ borderTop: '1px solid var(--c-border)', paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>{t('dashboard.widgets.githubActionsResets')}</span>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{formatResetDate(resetDate, i18n.language)}</span>
        </div>
      </div>
    </div>
  );
}
